import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import safeToast from '../utils/safeToast';
import { X, Radio, Send, Users, MessageSquare, Target, CheckCircle, Smartphone, Phone, Link } from 'lucide-react';
// Icon registry to preserve conditional UI usage paths
const _ICONS = { X, Radio, Send, Users, MessageSquare, Target, CheckCircle, Smartphone, Phone, Link } as const;
import { Lead } from '../types';
import { getAudience, prepareBroadcast, AudienceBucket } from '../api/broadcast';

interface BroadcastModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  cardOwnerId: string;
  leads?: Lead[];
}

export const BroadcastModal: React.FC<BroadcastModalProps> = ({
  isOpen, 
  onClose, 
  userName: _userName,
  cardOwnerId,
  leads = []
}) => {
  const [broadcastData, setBroadcastData] = useState({
    message: '',
    targetAudience: 'leads',
    includeContact: true,
    scheduleLater: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [lastSentCount, setLastSentCount] = useState<number>(0);
  const [lastSentSource, setLastSentSource] = useState<string>('selected audience');
  const [audience, setAudience] = useState<{ name: string; phone: string }[]>([]);
  const [mutualOnly, setMutualOnly] = useState(false);
  const [recentDays, setRecentDays] = useState(30);

  const selectedBucket: AudienceBucket = (
    broadcastData.targetAudience === 'leads' ? 'my-leads' :
    broadcastData.targetAudience === 'all' ? 'all-connections' :
    broadcastData.targetAudience === 'professional' ? 'professional-network' :
    broadcastData.targetAudience === 'local' ? 'local-contacts' : 'recent-connections'
  );
  // Important: hooks must not be conditionally skipped; run hooks before any early return
  // Get phone numbers from leads submitted through this card owner's email forms
  const getLeadPhoneNumbers = () => {
    return leads
      .filter(lead => 
        lead.cardOwnerId === cardOwnerId && 
        lead.source === 'email_form'
      )
      .map(lead => lead.phoneNumber)
      .filter(phone => phone && phone.trim() !== '')
      .filter((phone, index, array) => array.indexOf(phone) === index); // Remove duplicates
  };

  const leadPhoneNumbers = getLeadPhoneNumbers();

  React.useEffect(() => {
    if (!isOpen) return;
    // preview audience whenever selection changes (non-leads buckets)
    const load = async () => {
      try {
        const res: any = await getAudience({ ownerId: cardOwnerId, bucket: selectedBucket, mutualOnly, recentDays });
        const list = Array.isArray(res?.data) ? res.data : [];
        setAudience(list.map((x: any) => ({ name: x.name, phone: x.phone })));
      } catch {
        setAudience([]);
      }
    };
    if (broadcastData.targetAudience !== 'leads') load(); else setAudience([]);
  }, [isOpen, broadcastData.targetAudience, mutualOnly, recentDays]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (broadcastData.targetAudience === 'leads') {
      // Open SMS app with lead phone numbers
      if (leadPhoneNumbers.length > 0) {
        const phoneList = leadPhoneNumbers.join(',');
        const smsUrl = `sms:${phoneList}`;
        
        try {
          // Try to open SMS app with multiple recipients
          window.open(smsUrl, '_self');
          
          // Show success message
          setLastSentCount(leadPhoneNumbers.length);
          setLastSentSource('leads');
          setIsSuccess(true);
          setTimeout(() => {
            setIsSuccess(false);
            onClose();
            setBroadcastData({
              message: '',
              targetAudience: 'leads',
              includeContact: true,
              scheduleLater: false
            });
          }, 2000);
        } catch (error) {
          safeToast('warning', 'Unable to open SMS app. Please check if your device supports SMS functionality.');
        }
      } else {
  safeToast('warning', 'No phone numbers found from leads. You need leads with phone numbers to broadcast via SMS.');
      }
    } else {
      // Prepare numbers via backend (dedupe + chunking)
      try {
        setIsSubmitting(true);
        const prep: any = await prepareBroadcast({ ownerId: cardOwnerId, bucket: selectedBucket, mutualOnly, recentDays });
        const chunks: string[][] = Array.isArray(prep?.chunks) ? prep.chunks : [];
        const recipients: string[] = Array.isArray(prep?.recipients) ? prep.recipients : [];
        if (!recipients.length) {
          safeToast('warning', 'No recipients matched your selection.');
          setIsSubmitting(false);
          return;
        }
        // Open SMS composer for the first chunk; user can repeat for next chunks
  const first = chunks[0] || recipients.slice(0, 20);
        const phoneList = first.join(',');
        window.open(`sms:${phoneList}`, '_self');
        setIsSubmitting(false);
  setLastSentCount(first.length);
  setLastSentSource('selected audience');
  setIsSuccess(true);
        setTimeout(() => { setIsSuccess(false); onClose(); }, 2000);
      } catch {
        setIsSubmitting(false);
        safeToast('error', 'Failed to prepare recipients.');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setBroadcastData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const getEstimatedReach = () => {
    switch (broadcastData.targetAudience) {
      case 'leads':
        return leadPhoneNumbers.length.toString();
      case 'all':
        return '1,250';
      case 'professional':
        return '850';
      case 'local':
        return '420';
      default:
        return '180';
    }
  };

  if (isSuccess) {
    return createPortal(
      <div className="fixed inset-0 z-[99999] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div className="relative z-[100000] bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Broadcast Sent!</h3>
          <p className="text-gray-600">
            Your SMS app has been opened with {lastSentCount} contact numbers from your {lastSentSource}. Compose your message and send.
          </p>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[99999] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="relative z-[100000] bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        <button type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="mb-6">
          <div className="flex items-center mb-2">
            <Radio className="w-6 h-6 text-blue-600 mr-2" />
            <h3 className="text-xl font-bold text-gray-900">Broadcast Message</h3>
          </div>
          <p className="text-gray-600">Send a message to your professional network</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Target className="w-4 h-4 inline mr-1" />
              Target Audience
            </label>
            <select
              name="targetAudience"
              value={broadcastData.targetAudience}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="leads">My Leads ({leadPhoneNumbers.length} contacts)</option>
              <option value="all">All Connections</option>
              <option value="professional">Professional Network</option>
              <option value="local">Local Contacts</option>
              <option value="recent">Recent Connections</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              <Users className="w-3 h-3 inline mr-1" />
              Estimated reach: {getEstimatedReach()} people
            </p>
          </div>

          {broadcastData.targetAudience === 'leads' && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Smartphone className="w-5 h-5 text-blue-600 mr-2" />
                <h4 className="font-semibold text-blue-900">SMS Broadcast to Leads</h4>
              </div>
              <p className="text-sm text-blue-800 mb-3">
                This will open your phone's SMS app with {leadPhoneNumbers.length} contact numbers from people who submitted forms through your card.
              </p>
              {leadPhoneNumbers.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-blue-700 font-medium">Contact numbers to be added:</p>
                  <div className="bg-white p-2 rounded border max-h-20 overflow-y-auto">
                    <p className="text-xs text-gray-600 font-mono">
                      {leadPhoneNumbers.slice(0, 5).join(', ')}
                      {leadPhoneNumbers.length > 5 && ` ... and ${leadPhoneNumbers.length - 5} more`}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                  <p className="text-sm text-yellow-800">
                    <Phone className="w-4 h-4 inline mr-1" />
                    No phone numbers available from leads. You need leads with phone numbers to use SMS broadcast.
                  </p>
                </div>
              )}
            </div>
          )}

          {broadcastData.targetAudience !== 'leads' && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 flex items-center">
                  <Link className="w-4 h-4 mr-1 text-gray-500" />
                  Only show mutual connections
                </label>
                <input type="checkbox" checked={mutualOnly} onChange={(e) => setMutualOnly(e.target.checked)} />
              </div>
              {broadcastData.targetAudience === 'recent' && (
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-700">Recent window (days)</label>
                  <input type="number" min={1} max={180} value={recentDays} onChange={(e) => setRecentDays(Number(e.target.value || 30))} className="w-24 px-2 py-1 border rounded" />
                </div>
              )}
              <div>
                <p className="text-xs text-gray-600 mb-1">Preview ({audience.length} contacts)</p>
                <div className="bg-white rounded border max-h-24 overflow-y-auto p-2 text-xs text-gray-700">
                  {audience.slice(0, 6).map((a, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="truncate mr-2">{a.name || 'Contact'}</span>
                      <span className="font-mono">{a.phone}</span>
                    </div>
                  ))}
                  {audience.length > 6 && <div className="text-gray-400">...and {audience.length - 6} more</div>}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="scheduleLater"
                checked={broadcastData.scheduleLater}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Schedule for later (Coming soon)
              </span>
            </label>
          </div>

          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-xs text-blue-800">
              <Smartphone className="w-3 h-3 inline mr-1" />
              {broadcastData.targetAudience === 'leads' 
                ? 'This will open your SMS app with contact numbers from your leads.'
                : 'Your broadcast will be sent to your selected audience and may appear in their notifications. Large audiences may open in multiple batches.'}
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (broadcastData.targetAudience === 'leads' && leadPhoneNumbers.length === 0)}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                broadcastData.targetAudience === 'leads' ? <Smartphone className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />
              )}
              {isSubmitting ? 'Preparing...' : 
               broadcastData.targetAudience === 'leads' ? 'Open SMS App' : 'Open SMS'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};